import React from 'react';
import { Card, Table, Typography, Tabs, Drawer, Button, Image, Empty, Spin } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { useLocation } from 'react-router-dom';
import { listMeasuringInstrumentInventoriesMePaged } from '../../measuring-instrument-inventories/services/measuring-instrument-inventoryService';
import DeviceAssignmentCard from '../components/DeviceAssignmentCard';
import { listMeasuringInstrumentInspectorsMe } from '../../measuring-instrument-inspectors/services/measuring-instrument-inspectorService';

export default function InspectorDevicesPage(){
  const location = useLocation();
  const getTabFromSearch = React.useCallback((search: string): 'assignments' | 'inventory' => {
    const tab = new URLSearchParams(search).get('tab');
    return tab === 'assignments' ? 'assignments' : 'inventory';
  }, []);
  const [data, setData] = React.useState<any[]>([]);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [inventorySortOrder, setInventorySortOrder] = React.useState<'ascend' | 'descend' | undefined>('descend');
  const [activeTab, setActiveTab] = React.useState<'assignments' | 'inventory'>(() => getTabFromSearch(location.search));
  const [photoDrawerOpen, setPhotoDrawerOpen] = React.useState(false);
  const [photoUrls, setPhotoUrls] = React.useState<string[]>([]);
  const [photoLoading, setPhotoLoading] = React.useState(false);
  const [photoDrawerTitle, setPhotoDrawerTitle] = React.useState('Cihaz Fotoğrafları');
  const getInventorySortParam = React.useCallback((order?: 'ascend' | 'descend') => {
    if (!order) return undefined;
    return order === 'descend' ? 'nextCalibrationDate,desc' : 'nextCalibrationDate,asc';
  }, []);
  const handleShowPhotos = async (inventoryId?: number, deviceName?: string) => {
    if (!inventoryId) return;
    setPhotoDrawerTitle(deviceName ? `${deviceName} Fotoğrafları` : 'Cihaz Fotoğrafları');
    setPhotoDrawerOpen(true);
    setPhotoLoading(true);
    try {
      const response = await listMeasuringInstrumentInspectorsMe(`measuringInstrumentInventoryId.equals=${inventoryId}`);
      const latestRecord =
        Array.isArray(response) && response.length
          ? response.reduce((prev, curr) => {
              const prevId = typeof prev?.id === 'number' ? prev.id : 0;
              const currId = typeof curr?.id === 'number' ? curr.id : 0;
              return currId > prevId ? curr : prev;
            }, response[0])
          : null;
      const urls = latestRecord?.photoUrls;
      setPhotoUrls(Array.isArray(urls) ? urls.filter((url: any) => typeof url === 'string' && url) : []);
    } catch (error) {
      console.error('DeviceAssignment photos fetch failed', error);
      setPhotoUrls([]);
    } finally {
      setPhotoLoading(false);
    }
  };

  const fetchData = React.useCallback(async () => {
    try {
      const sort = getInventorySortParam(inventorySortOrder);
      const res = await listMeasuringInstrumentInventoriesMePaged(page - 1, pageSize, sort);
      setData(res.content || []);
      setTotal(res.totalElements || 0);
    } catch {
      setData([]);
      setTotal(0);
    }
  }, [getInventorySortParam, inventorySortOrder, page, pageSize]);
  React.useEffect(() => {
    if (activeTab !== 'inventory') return;
    fetchData();
  }, [activeTab, fetchData]);
  React.useEffect(() => {
    setActiveTab(getTabFromSearch(location.search));
  }, [getTabFromSearch, location.search]);

  const handleInventoryTableChange: TableProps<any>['onChange'] = (_pagination, _filters, sorter) => {
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrder = (nextSorter?.order ?? undefined) as 'ascend' | 'descend' | undefined;
    if (nextOrder !== inventorySortOrder) {
      setInventorySortOrder(nextOrder);
      setPage(1);
    }
  };

  const columns = [
    {
      title: 'Ölçüm Cihazı',
      dataIndex: 'measuringInstrument.name',
      render: (_: any, record: any) => record?.measuringInstrument?.name ?? '',
    },
    { title: 'Seri Numarası', dataIndex: 'serialNumber' },
    {
      title: 'Kalibrasyon Tarihi',
      dataIndex: 'calibrationDate',
      render: (v: string) => (v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : ''),
    },
    {
      title: 'Kalibrasyon Geçerlilik Tarihi',
      dataIndex: 'nextCalibrationDate',
      key: 'nextCalibrationDate',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: inventorySortOrder,
      render: (v: string) => (v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : ''),
    },
    {
      title: 'İşlemler',
      render: (_: any, record: any) => {
        const inventoryId = record?.id ?? record?.measuringInstrumentInventoryId;
        const deviceName = record?.measuringInstrument?.name;
        return (
          <Button  onClick={() => handleShowPhotos(inventoryId, deviceName)}>
            Görüntüle
          </Button>
        );
      },
    },
  ];
  const tabs = [
    {
      key: 'inventory',
      label: 'Cihazlarım',
      children: (
        <Card
          className='br-30px'
          title={
            <Typography.Title level={4} style={{ margin: 0 }}>
              Cihazlarım <Typography.Text type='secondary'><strong>({total})</strong> kayıt listelendi</Typography.Text>
            </Typography.Title>
          }
        >
          <div>
            <Table
              rowKey={(r:any)=>r.id}
              dataSource={data}
              columns={columns as any}
              onChange={handleInventoryTableChange}
              pagination={{ current: page, pageSize, total, onChange:(p,s)=>{ setPage(p); setPageSize(s || 10);} }}
            />
          </div>
        </Card>
      ),
    },
    {
      key: 'assignments',
      label: 'Cihaz Atamaları',
      children: <DeviceAssignmentCard query='confirmedAt.specified=false&rejectedAt.specified=false' />,
    },
  ];

  return (
    <>
        <Tabs
        style={{margin: 16, marginTop: 0}}
          activeKey={activeTab}
          defaultActiveKey='inventory'
          onChange={(key) => setActiveTab(key as 'assignments' | 'inventory')}
          items={tabs}
        />
      <Drawer
        open={photoDrawerOpen}
        onClose={() => setPhotoDrawerOpen(false)}
        width={520}
        title={
          <div>
            <div>{photoDrawerTitle}</div>
            <Typography.Text type='secondary'>
              Cihazın teslim anındaki fotoğrafları aşağıda bulunmaktadır
            </Typography.Text>
          </div>
        }
        bodyStyle={{ padding: 16, minHeight: 200 }}
      >
        {photoLoading ? (
          <Spin />
        ) : photoUrls.length ? (
          <Image.PreviewGroup>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {photoUrls.map((url) => (
                <Image key={url} src={url} alt='Cihaz fotoğrafı' width='100%' style={{ objectFit: 'cover', borderRadius: 8 }} />
              ))}
            </div>
          </Image.PreviewGroup>
        ) : (
          <Empty description='Fotoğraf bulunamadı' />
        )}
      </Drawer>
    </>
  );
}
